/**
 * iSALUS Rewards+ — the single, platform-wide loyalty program.
 *
 * One centralized definition shared by the iOS/Android app, the tablet layouts,
 * the desktop and mobile website, the checkout, and the admin dashboard.
 *
 * Rewards+ discounts apply ONLY to the iSALUS service (platform) fee.
 * They never reduce the cleaner's earnings.
 */

export type RewardsTierKey = "new" | "bronze" | "silver" | "gold" | "platinum";

export type RewardsTier = {
  key: RewardsTierKey;
  label: string;
  emoji: string;
  /** Completed bookings required to reach this level. */
  min: number;
  /** Percentage taken off the iSALUS service fee (not off the cleaner's pay). */
  feeDiscountPct: number;
  /** Membership status line shown on badges and dashboards. */
  message: string;
  /** Tailwind classes bound to reward tokens in styles.css. */
  tone: string;
  /** Gradient used for the badge + progress fill. */
  gradient: string;
  /** Headline benefit unlocked at this level. */
  benefit: string;
};

/** Finalized production configuration. Platinum is the highest level. */
export const REWARDS_TIERS: RewardsTier[] = [
  {
    key: "new",
    label: "Rewards+ Member",
    emoji: "✨",
    min: 0,
    feeDiscountPct: 0,
    message: "Keep booking to unlock Bronze Member status.",
    tone: "text-muted-foreground",
    gradient: "linear-gradient(90deg, var(--muted-foreground), var(--primary))",
    benefit: "Keep booking to unlock Bronze Member status",
  },
  {
    key: "bronze",
    label: "Bronze Member",
    emoji: "🥉",
    min: 25,
    feeDiscountPct: 15,
    message: "Welcome to the iSALUS community",
    tone: "text-warning",
    gradient: "linear-gradient(90deg, oklch(0.62 0.11 55), oklch(0.78 0.13 70))",
    benefit: "15% off iSALUS service fees",
  },
  {
    key: "silver",
    label: "Silver Member",
    emoji: "🥈",
    min: 100,
    feeDiscountPct: 25,
    message: "Trusted iSALUS Client",
    tone: "text-muted-foreground",
    gradient: "linear-gradient(90deg, oklch(0.70 0.02 250), oklch(0.88 0.02 250))",
    benefit: "25% off iSALUS service fees",
  },
  {
    key: "gold",
    label: "Gold Member",
    emoji: "🥇",
    min: 250,
    feeDiscountPct: 35,
    message: "iSALUS Preferred Client",
    tone: "text-warning",
    gradient: "linear-gradient(90deg, oklch(0.72 0.14 85), oklch(0.87 0.15 95))",
    benefit: "35% off iSALUS service fees",
  },
  {
    key: "platinum",
    label: "Platinum VIP Member",
    emoji: "💎",
    min: 500,
    feeDiscountPct: 45,
    message: "iSALUS VIP Member",
    tone: "text-accent",
    gradient: "linear-gradient(90deg, var(--primary), var(--accent))",
    benefit: "45% off iSALUS service fees",
  },
];

/** Admin-configurable overrides (thresholds + discounts), stored in app_settings. */
export type RewardsConfig = Partial<
  Record<Exclude<RewardsTierKey, "new">, { min: number; feeDiscountPct: number }>
>;

/** Apply an admin configuration over the finalized defaults. */
export function resolveTiers(config?: RewardsConfig | null): RewardsTier[] {
  if (!config) return REWARDS_TIERS;
  return REWARDS_TIERS.map((t) => {
    const o = t.key === "new" ? undefined : config[t.key];
    if (!o) return t;
    return {
      ...t,
      min: Number.isFinite(o.min) ? Math.max(1, Math.floor(o.min)) : t.min,
      feeDiscountPct: Number.isFinite(o.feeDiscountPct)
        ? Math.min(100, Math.max(0, o.feeDiscountPct))
        : t.feeDiscountPct,
      benefit: `${
        Number.isFinite(o.feeDiscountPct) ? o.feeDiscountPct : t.feeDiscountPct
      }% off iSALUS service fees`,
    };
  }).sort((a, b) => a.min - b.min);
}

export type RewardsProgress = {
  tier: RewardsTier;
  next: RewardsTier | null;
  completed: number;
  /** Bookings still needed to reach the next level (0 at max level). */
  remaining: number;
  /** 0-1 progress toward the next level (1 at max level). */
  ratio: number;
  /** 0-100, rounded. */
  percent: number;
  /** Target used for the "x / y bookings" label. */
  goal: number;
  message: string;
  /** Discount currently applied to the iSALUS service fee. */
  feeDiscountPct: number;
};

export function rewardsTierFor(completed: number, tiers = REWARDS_TIERS): RewardsTier {
  return [...tiers].reverse().find((t) => completed >= t.min) ?? tiers[0]!;
}

export function nextRewardsTier(completed: number, tiers = REWARDS_TIERS): RewardsTier | null {
  return tiers.find((t) => t.min > completed) ?? null;
}

export function rewardsProgress(completedInput: number, tiers = REWARDS_TIERS): RewardsProgress {
  const completed = Math.max(0, Math.floor(completedInput || 0));
  const tier = rewardsTierFor(completed, tiers);
  const next = nextRewardsTier(completed, tiers);

  if (!next) {
    return {
      tier,
      next: null,
      completed,
      remaining: 0,
      ratio: 1,
      percent: 100,
      goal: tier.min,
      message: "You've reached the highest iSALUS Rewards+ level.",
      feeDiscountPct: tier.feeDiscountPct,
    };
  }

  const floor = tier.key === "new" ? 0 : tier.min;
  const span = Math.max(1, next.min - floor);
  const ratio = Math.min(1, Math.max(0, (completed - floor) / span));
  const remaining = Math.max(0, next.min - completed);

  return {
    tier,
    next,
    completed,
    remaining,
    ratio,
    percent: Math.round(ratio * 100),
    goal: next.min,
    message:
      tier.key === "new"
        ? `${remaining} more booking${remaining === 1 ? "" : "s"} to unlock Bronze Member status`
        : `${remaining} more cleaning${remaining === 1 ? "" : "s"} until ${next.label}`,
    feeDiscountPct: tier.feeDiscountPct,
  };
}

/**
 * Rewards+ saving on a service fee, in the same units as `serviceFee`.
 * Never applied to the cleaner's payout.
 */
export function rewardsFeeSaving(serviceFee: number, feeDiscountPct: number): number {
  if (!(serviceFee > 0) || !(feeDiscountPct > 0)) return 0;
  return Math.round(serviceFee * (feeDiscountPct / 100) * 100) / 100;
}

/** Cents-precision variant used by checkout and the Stripe layer. */
export function rewardsFeeSavingCents(serviceFeeCents: number, feeDiscountPct: number): number {
  if (!(serviceFeeCents > 0) || !(feeDiscountPct > 0)) return 0;
  return Math.round(serviceFeeCents * (feeDiscountPct / 100));
}

/** Short, upbeat line shown right after a completed cleaning. */
export function rewardsThankYouMessage(completed: number): string {
  const p = rewardsProgress(completed);
  if (!p.next) return "Thank you for choosing iSALUS. You're a Platinum VIP Member.";
  return `Thank you for choosing iSALUS. You are now ${p.remaining} cleaning${
    p.remaining === 1 ? "" : "s"
  } closer to ${p.next.label}.`;
}

/** Premium, non-childish celebration copy for a newly unlocked level. */
export function celebrationFor(tier: RewardsTier): { title: string; body: string } {
  switch (tier.key) {
    case "bronze":
      return {
        title: "Congratulations! 🎉 You've reached Bronze Member status.",
        body: "Welcome to the iSALUS community — 15% off iSALUS service fees from now on.",
      };
    case "silver":
      return {
        title: "Congratulations! 🎉 You're now a Silver Trusted iSALUS Client.",
        body: "25% off iSALUS service fees on every booking.",
      };
    case "gold":
      return {
        title: "Amazing! 🎉 You've reached Gold Preferred Client status.",
        body: "35% off iSALUS service fees on every booking.",
      };
    case "platinum":
      return {
        title: "Welcome to Platinum VIP! 💎",
        body: "You've reached the highest iSALUS Rewards+ level — 45% off iSALUS service fees.",
      };
    default:
      return {
        title: "Welcome to iSALUS Rewards+",
        body: "Keep booking to unlock Bronze Member status.",
      };
  }
}

const SEEN_KEY = "isalus_rewards_tier_seen";

/** Returns the tier just unlocked (for the celebration), and records it. */
export function consumeTierUnlock(completed: number): RewardsTier | null {
  if (typeof window === "undefined") return null;
  const tier = rewardsTierFor(completed);
  let seen: string | null = null;
  try {
    seen = window.localStorage.getItem(SEEN_KEY);
  } catch {
    return null;
  }
  try {
    window.localStorage.setItem(SEEN_KEY, tier.key);
  } catch {
    /* ignore */
  }
  if (!seen || seen === tier.key) return null;
  const order = REWARDS_TIERS.map((t) => t.key);
  return order.indexOf(tier.key) > order.indexOf(seen as RewardsTierKey) ? tier : null;
}
